<script setup lang="ts">
	import { computed, ref, watch } from 'vue'
	import { useToast } from 'vue-toastification'

	interface Props {
		modelValue: any
		maxKb?: number
		label?: string
		dropZoneClass?: string
		acceptTypes?: string[]
		labelClass?: string
	}
	interface Emits {
		(event: 'update:modelValue', value: any): void
	}

	const props = withDefaults(defineProps<Props>(), {
		label: '',
		maxKb: 20480,
	})
	const emit = defineEmits<Emits>()

	if (props.acceptTypes) {
		props.acceptTypes.forEach((type) => {
			if (!type.includes('/')) {
				throw Error(
					`Invalid MIME type: ${type}. MIME types must be in the format 'type/subtype'`
				)
			}
		})
	}

	const file = ref<File | null>()
	const fileInput = ref<HTMLInputElement>()
	const draggingOver = ref(false)
	const toast = useToast()

	const fileSize = computed(() => {
		return {
			kilobytes: file.value ? file.value?.size / 1024 : undefined,
			get megabytes() {
				return this.kilobytes ? this.kilobytes / 1024 : undefined
			},
			get available() {
				return this.kilobytes && this.kilobytes
			},
		}
	})

	const maxSizes = computed(() => {
		return {
			kilobytes: props.maxKb * 1024,
			megabytes: Number((props.maxKb / 1024).toFixed(2)),
		}
	})

	const validateFileType = (file: File) => {
		if (props.acceptTypes?.includes(file?.type)) {
			if (file.size <= maxSizes.value.kilobytes) {
				return true
			} else {
				toast.error(
					`El archivo no puede pesar más de ${maxSizes.value.megabytes} MB`
				)
				return false
			}
		} else {
			toast.error(
				`Sólo se pueden subir archivos tipo: ${
					props.acceptTypes?.join(', ') || ''
				}`
			)
			return false
		}
	}

	const dragLeaveHandler = (event: DragEvent) => {
		event.preventDefault()
		draggingOver.value = false
	}
	const dragOverHandler = (event: DragEvent) => {
		event.preventDefault()
		draggingOver.value = true
	}
	const dropHandler = (event: DragEvent) => {
		event.preventDefault()

		if (event.dataTransfer) {
			const files = event.dataTransfer.files

			if (files.length > 1) {
				toast.error('Solo se puede subir un archivo a la vez')
			} else {
				if (files[0] && validateFileType(files[0])) {
					file.value = files[0]
				}
			}
		}
		draggingOver.value = false
	}
	const onChangeHandler = () => {
		if (fileInput.value?.files?.length) {
			if (validateFileType(fileInput.value.files![0])) {
				file.value = fileInput.value.files![0] || undefined
			}
		}
	}
	const removeFile = () => {
		file.value = null
	}

	const transformedAccept = computed(() => {
		if (props.acceptTypes) {
			return props.acceptTypes.join(', ')
		}
		return ''
	})

	watch(file, (file) => {
		emit('update:modelValue', file)
	})
</script>

<template>
	<fieldset class="grid gap-[0.625rem]">
		<h3
			v-if="label"
			:class="['text-base font-medium', labelClass]"
		>
			{{ label }}
		</h3>

		<div class="flex items-center">
			<p
				v-tooltip="file && file.name"
				class="flex-1 text-sm border border-r-0 truncate rounded-l py-[0.313rem] px-[1.625rem]"
			>
				{{ file ? file.name : 'Seleccione el archivo a subir' }}
			</p>

			<label
				class="py-[0.313rem] px-[0.625rem] border border-[#B5B5B5] bg-[#E7E7E7] text-sm rounded-r cursor-pointer"
			>
				Buscar archivo

				<input
					class="hidden"
					ref="fileInput"
					type="file"
					name="file"
					:accept="transformedAccept"
					@change="onChangeHandler"
				/>
			</label>
		</div>

		<div
			id="drop_zone"
			@dragleave="dragLeaveHandler"
			@dragover.prevent="dragOverHandler"
			@drop.prevent="dropHandler"
			:class="[
				'flex w-full justify-center items-center py-[2.125rem] bg-[#EFF9FF] border border-[#B3B3B3] border-dashed text-[#707070] fade-transition',
				draggingOver &&
					'!border-[#25702b] !bg-[#fbfeff] !bg-transparent blur-[2px]',
				dropZoneClass,
			]"
		>
			<div
				:class="[
					'flex w-full flex-col items-center pb-4 gap-[0.625rem] text-center',
					draggingOver && '-z-10',
				]"
			>
				<span
					:class="[
						'material-symbols-outlined text-5xl w-fit',
						file ? 'text-[#25702b]' : 'text-[#B5B5B5]',
					]"
				>
					{{ file ? 'check_circle' : 'cloud_upload' }}
				</span>

				<p
					:class="[
						'text-sm w-fit font-medium whitespace-nowrap transition-colors duration',
						file && 'underline cursor-pointer ',
					]"
					@click="removeFile"
				>
					{{ file ? 'Descartar archivo' : `Arrastre y suelte su archivo aquí` }}
				</p>

				<span>{{
					fileSize.available ?
						`(archivo de ${fileSize.kilobytes?.toFixed(2)}KB)`
					:	`(máximo ${maxSizes.megabytes} MB)`
				}}</span>
			</div>
		</div>
	</fieldset>
</template>

<style scoped></style>
